import { getLanguageById } from "@/features/playground/config/languages";

/**
 * Which Playground language the reader's right pane falls back to when the
 * lesson has not said (`LN-RDR01`, docs/35 §5 item 1): `r`, the top bar's
 * Editor button and the sheet open on it when the lesson has no program.
 *
 * It is a map from the track slug and not a field on the wire, because the
 * values it maps to are the Playground's own language ids and those live in
 * this app (`features/playground/config/languages.ts`, itself generated from
 * `language-contract.ts`, docs/24 §3.1). A backend field would be a second
 * place for a language id to be written, and the one rule about language ids is
 * that the contract is where they come from.
 *
 * The keys are the slugs the manifests ship (`course-content/<course>/manifest.json`
 * `trackSlug`): `c`, `js` and `stl` (`RV-10`). The map once knew `javascript`
 * and `cpp-stl` instead, and an unknown slug fell back to C, so the JavaScript
 * and STL tracks silently opened `main.c` and compiled their programs with gcc.
 * An unmapped track now answers null, and `track-language.test.ts` reads every
 * manifest in the workspace and fails on one this map does not name.
 *
 * Since `RV-12` a program the lesson hands the pane carries its own language
 * (the block's `language-*` class, or the Run link's `/playground/<lang>`
 * path), so this is the fallback, not the rule: an STL lesson's C program runs
 * as C.
 */
const TRACK_LANGUAGE: Record<string, string> = {
  c: "c",
  js: "javascript",
  stl: "cpp",
};

export function playgroundLanguageForTrack(trackSlug: string): string | null {
  const id = TRACK_LANGUAGE[trackSlug.toLowerCase()];
  // A mapping that names a language the Playground does not serve would leave
  // the pane empty, so the contract has the last word.
  return id && getLanguageById(id) ? id : null;
}

/**
 * `RV-11`: the runner version a Learn problem is graded on, by language, when
 * the lesson's program names none: the version its reference solution was
 * verified on (`course-content/tools/lib/problem-language.mjs`,
 * `GRADED_VERSION_BY_LANGUAGE`; a statement marked `c++20` comes to the pane as
 * the program's own version). The live catalogue marks no default and lists
 * `c++14` first, so its first row would submit an STL problem on C++14. C names
 * none and keeps the catalogue's first, the pick the seeder has always made.
 */
export const GRADED_VERSION_BY_LANGUAGE: Readonly<Record<string, string>> = {
  cpp: "c++17",
  javascript: "22",
};

/** A code block's `language-*` class, in the Playground's ids. */
const BLOCK_LANGUAGE: Record<string, string> = {
  c: "c",
  cpp: "cpp",
  "c++": "cpp",
  python: "python",
  py: "python",
  javascript: "javascript",
  js: "javascript",
};

/** The Playground language a block's `language-*` class names, if it serves one. */
export function blockLanguageId(blockLang: string | undefined): string | undefined {
  const id = blockLang ? BLOCK_LANGUAGE[blockLang.toLowerCase()] : undefined;
  return id && getLanguageById(id) ? id : undefined;
}

/** Languages whose programs start at a `main` function, so a fragment cannot run. */
const NEEDS_MAIN = new Set(["c", "cpp"]);

/**
 * Which code blocks carry a Run button in the reader (`LN-UI05`, `RV-12`).
 *
 * A block runs in its own language, because the pane takes the language from
 * the program it is handed. It runs when it is a whole program: a C or C++
 * fragment without `main` would only answer with a linker error, which teaches
 * nothing about the fragment, while a C program in an STL lesson (the C side of
 * a "from C to C++" comparison) runs as C. An interpreted language has no
 * `main` to tell a program from a fragment by, so a block in one runs only on
 * its own track (or on a track nobody mapped): the C track's Python and
 * JavaScript asides are one-line comparisons, not programs. Shell sessions,
 * outputs and unlabelled blocks keep Copy alone.
 */
export function canRunBlockInTrack(
  trackLanguageId: string | null,
  blockLang: string | undefined,
  code: string,
): boolean {
  const id = blockLanguageId(blockLang);
  if (!id || !code.trim()) return false;
  if (NEEDS_MAIN.has(id)) return /\bmain\s*\(/.test(code);
  return trackLanguageId === null || id === trackLanguageId;
}
